<template>
  <div
    class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
  >
    <div
      class="flex cursor-pointer items-center justify-between p-5"
      @click="whatsappExpanded = !whatsappExpanded"
    >
      <div class="flex items-center gap-4">
        <div
          class="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30"
        >
          <i
            class="fa-brands fa-whatsapp text-2xl text-green-600 dark:text-green-400"
          ></i>
        </div>
        <div>
          <h3 class="text-lg font-medium text-gray-800 dark:text-white">
            WhatsApp Marketing
          </h3>
          <div class="flex items-center gap-2 mt-1">
            <span
              class="inline-flex h-2 w-2 rounded-full"
              :class="isWhatsAppConnected ? 'bg-green-500' : 'bg-gray-400'"
            >
            </span>
            <span
              class="text-sm font-medium"
              :class="
                isWhatsAppConnected
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              {{ isWhatsAppConnected ? "Connected" : "Not Connected" }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <div class="flex items-center">
          <div
            style="cursor: pointer"
            class="relative inline-block w-8 h-6 rounded-full"
          >
            <input
              :disabled="!isWhatsAppConnected"
              id="whatsappAutoPilotSwitch"
              style="cursor: pointer"
              type="checkbox"
              v-model="whatsappAutoPilotEnabled"
              class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
            />
            <label
              htmlFor="whatsappAutoPilotSwitch"
              style="cursor: pointer"
              class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
            >
              <div
                class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                data-ripple-dark="true"
              ></div>
            </label>
          </div>
          <span
            class="ml-5 text-sm font-medium text-gray-700 dark:text-gray-300"
            >Auto Pilot</span
          >
        </div>
        <button
          class="text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <i
            class="fas"
            :class="whatsappExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>
      </div>
    </div>

    <!-- Connection Button (when collapsed) -->
    <div v-if="!whatsappExpanded && !isWhatsAppConnected" class="px-5 pb-5">
      <button
        @click.stop="connectWhatsApp"
        class="w-full rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
      >
        <i class="fa-brands fa-whatsapp mr-2"></i> Connect WhatsApp
      </button>
    </div>

    <!-- Expanded Content -->
    <div
      v-if="whatsappExpanded"
      class="space-y-6 border-t border-gray-200 p-6 dark:border-gray-600"
    >
      <div v-if="!isWhatsAppConnected">
        <p class="mb-4 dark:text-gray-300">
          Connect your WhatsApp Business account for messaging campaigns.
        </p>
        <button
          @click="connectWhatsApp"
          class="rounded-lg bg-green-600 px-6 py-3 font-medium text-white transition-colors hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
        >
          Connect WhatsApp Account
        </button>
      </div>

      <div v-else class="space-y-6">
        <!-- Connection Status -->
        <div class="flex items-center justify-between">
          <div class="flex items-center text-green-600 dark:text-green-400">
            <svg class="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <span>Connected to {{ whatsAppUserInfo.phone }}</span>
          </div>
          <button
            @click="disconnectWhatsApp"
            class="rounded-lg bg-red-600 px-3 py-1 text-sm text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
          >
            Disconnect
          </button>
        </div>

        <!-- WhatsApp configuration components would go here -->
        <div class="space-y-4">
          <h4 class="font-medium text-gray-700 dark:text-gray-300">
            WhatsApp Settings
          </h4>
          <!-- Settings form would go here -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "WhatsAppMarketing",
  data() {
    return {
      whatsappExpanded: false,
      isWhatsAppConnected: false,
      whatsappAutoPilotEnabled: false,
      saving: false,
      saveSuccess: false,
      whatsAppUserInfo: {},
    };
  },
  computed: {
    ...mapGetters(["isWhatsAppConnected", "whatsAppUserInfo"]),
  },
  methods: {
    connectWhatsApp() {
      this.$emit("connect-whatsapp");
    },
    disconnectWhatsApp() {
      this.$emit("disconnect-whatsapp");
    },
    handleWhatsAppSave(integrationData) {
      this.$emit("save-whatsapp-settings", integrationData);
      this.saveSuccess = true;
      setTimeout(() => {
        this.saveSuccess = false;
      }, 3000);
    },
  },
};
</script>

<style scoped>
button,
.cursor-pointer {
  transition: all 0.2s ease;
}

/* Card hover effects */
.cursor-pointer:hover {
  transform: translateY(-1px);
}
</style>
